'use strict';
import get from 'lodash/get';
import isArray from 'lodash/isArray';
import isFunction from 'lodash/isFunction';
import {createEventHandler, mapPropsStream} from 'recompose';
import {Observable} from 'rxjs';
import 'rxjs/add/operator/combineLatest';
import 'rxjs/add/operator/debounceTime';
import 'rxjs/add/operator/takeUntil';
import 'rxjs/add/operator/withLatestFrom';
import {INIT} from '../../../../components/forms/Form';
import {INPUT_TYPES} from '../../../../forms/enums/InputTypes';
import {getIsDirty, getResponseValue, valueIsEqual} from '../../../utils';
import {FormResponseView} from './FormResponseView';


const StreamedComponent = (props$) => {
  const {
    handler: onChange,
    stream: change$,
  } = createEventHandler();

  const fields$ = props$
    .map(props => {
      const model = props.model;
      return isArray(get(model, 'fields')) ? model.fields : [];
    })
    .distinctUntilChanged();

  const isEditing$ = props$
    .map(props => props.isEditing)
    .distinctUntilChanged();

  const onReset$ = isEditing$
    .withLatestFrom(props$, fields$)
    .map(([isEditing, props, fields]) => getInitialModelState(props, fields));

  const modelState$ = fields$
    .switchMap(fields => {
      const states$ = [];
      const changeHandlers = {};
      fields.forEach((field, idx) => {
        const prop = field.id;

        states$.push(props$
          .map(props => getResponseValue(field, props.model, null, idx))
          .distinctUntilChanged()
          .combineLatest(
            change$
              .filter(([fieldId, _]) => prop === fieldId)
              .startWith([prop, INIT]),
            (prevValue, [fieldId, event]) => {
              const nextState = {prop: fieldId};

              if (event === INIT) {
                // startWith case. Just forward the initial state.
                nextState.internalModel = prevValue;
                nextState.isChanging = false;

              } else {
                const nextValue = event.target.value;
                const isToggled = !valueIsEqual(field, prevValue, nextValue);
                nextState.internalModel = isToggled ? nextValue : prevValue;
                nextState.isChanging = isToggled;

              }

              if (field.useError) {
                nextState.hasError = !event.target.isValid;
              }

              return nextState;
            }
          ));
      });

      const modelState$ = Observable
        .combineLatest(
          ...states$,
          (...states) => {
            const nextState = {
              internalModel: {},
              isChanging: {},
              hasError: {},
            };

            states.forEach(state => {
              nextState.internalModel[state.prop] = state.internalModel;
              nextState.isChanging[state.prop] = state.isChanging;
              nextState.hasError[state.prop] = state.hasError;
            });

            nextState.isDirty = getIsDirty(nextState.isChanging);

            return nextState;
          }
        )
        /* Skip the initialization */
        .skip(1);

      return Observable
        .merge(
          modelState$,
          onReset$,
        )
        .map(modelState => ({
          onChange: changeHandlers,
          ...modelState,
        }));
    })
    .share()
    .takeUntil(props$.last());

  props$
    .map(props => props.triggerSave$)
    .filter(triggerSave$ => !!triggerSave$)
    .distinctUntilChanged()
    .switchMap(triggerSave$ => triggerSave$)
    .withLatestFrom(props$, modelState$, fields$)
    .debounceTime(300)
    .takeUntil(props$.last())
    .subscribe(([event, props, modelState, fields]) => {
      if (event) {
        event.stopPropagation();
        event.preventDefault();
      }

      console.log('onSave', props, modelState);

      // combine internalModel with props.model.responses and submit to props.onSave
      if (modelState.isDirty) {
        if (isFunction(props.onSave)) {
          props.onSave(serializeModel(fields, modelState, props.model));
        }
      } else {
        if (isFunction(props.onCancel)) {
          props.onCancel();
        }
      }
    });

  modelState$
    .map(props => props.isDirty)
    .distinctUntilChanged()
    .withLatestFrom(props$)
    .takeUntil(props$.last())
    .subscribe(([isDirty, props]) => {
      if (isFunction(props.onIsDirtyChange)) {
        props.onIsDirtyChange(isDirty);
      }
    });

  return props$
    .combineLatest(
      fields$,
      modelState$,
      (props, fields, modelState) => ({
        ...props,
        ...modelState,
        fields,

        // TODO: impl or figure out?
        validationMessages: {},

        onChange,
      })
    );
};

export const FormResponseComponent = mapPropsStream(StreamedComponent)(FormResponseView);

function getInitialModelState(props, fields) {
  const nextState = {
    internalModel: {},
    isChanging: {},
    hasError: {},
  };

  fields.forEach((field, idx) => {
    const prop = field.id;

    nextState.internalModel[prop] = getResponseValue(field, props.model, prop, idx);
    nextState.isChanging[prop] = false;
    nextState.hasError[prop] = false;
  });

  nextState.isDirty = getIsDirty(nextState.isChanging);

  return nextState;
}

function serializeModel(fields, modelState, model) {
  const responses = model.responses || [];

  const prevModel = fields.reduce((acc, field, idx) => {
    const prop = field.id;
    acc[prop] = responses[idx];
    return acc;
  }, {});

  const dirtyResponses = fields.map(field => {
    const prop = field.id;
    const prevResponse = prevModel[prop];

    if (modelState.isChanging[prop]) {
      switch (field.type) {
        case INPUT_TYPES.text_long:
        case INPUT_TYPES.text_short:
          return {
            ...(prevResponse || {}),
            result: {
              value: modelState.internalModel[prop] === '' ? null : modelState.internalModel[prop],
            },
          };

        default:
          return {
            ...(prevResponse || {}),
            result: modelState.internalModel[prop],
          };
      }

    } else {
      return prevResponse || null;
    }
  });

  return {
    ...model,
    responses: dirtyResponses,
  };
}



// WEBPACK FOOTER //
// ./src/js/form-builder/components/response/FormResponse/FormResponseComponent.js